/**
 * Ordinary shape check: one @, a local part, and a domain with a TLD.
 * Deliberately NOT an RFC 5322-complete regex — those reject valid addresses.
 * The local-part rules live in ValidateEmailLocalPart, on top of this.
 */
const emailShapeRegex = /^[\p{L}\p{N}._%+-]+@[\p{L}\p{N}.-]+\.[\p{L}]{2,}$/u;

/** Reasons an email can be rejected. Maps to an i18n key via EmailErrorKey. */
export enum EmailError {
    /** Empty / whitespace-only. */
    Missing = "Missing",
    /** No @, no TLD, whitespace inside, two @, etc. */
    Shape = "Shape",
    /** Local part starts with, ends with, or contains consecutive dots. */
    LocalPartDots = "LocalPartDots",
}

/** i18n keys for each rejection reason. */
export const EmailErrorKey: Record<EmailError, string> = {
    [EmailError.Missing]: "EmailIsRequired",
    [EmailError.Shape]: "EmailInvalidFormat",
    [EmailError.LocalPartDots]: "EmailInvalidDots",
};

/**
 * The three extra constraints on the local part (the piece before the @).
 * MimeKit refuses these in every compliance mode, so the address is unsendable
 * and a queued mail can never be delivered — see EmailInvalidDots.
 *
 * A single dot between characters is valid and must keep working.
 */
export function ValidateEmailLocalPart(localPart: string): boolean {
    if (localPart.startsWith(".")) return false;
    if (localPart.endsWith(".")) return false;
    if (localPart.includes("..")) return false;
    return true;
}

/**
 * Validates an email address and reports WHY it was rejected, so the field can
 * show an actionable message. Trims first: pasted addresses routinely carry a
 * trailing space or newline, and users should not be punished for that.
 * Returns undefined when the address is valid.
 */
export function GetEmailError(mail: string | undefined | null): EmailError | undefined {
    const trimmed = (mail ?? "").trim();

    if (trimmed === "") return EmailError.Missing;
    if (!emailShapeRegex.test(trimmed)) return EmailError.Shape;

    // Safe: emailShapeRegex guarantees exactly one @, so this split has 2 parts.
    const localPart = trimmed.slice(0, trimmed.lastIndexOf("@"));
    if (!ValidateEmailLocalPart(localPart)) return EmailError.LocalPartDots;

    return undefined;
}

/**
 * Returns the i18n key for the error on this address, or undefined if valid.
 * Callers pass the result through t() to render it on the field.
 */
export function GetEmailErrorKey(mail: string | undefined | null): string | undefined {
    const error = GetEmailError(mail);
    return error === undefined ? undefined : EmailErrorKey[error];
}

/**
 * Keeps the original signature (undefined in -> undefined out) so existing
 * callers are unaffected, but now enforces the local-part rules too.
 */
export function ValidateEmail(mail: string | undefined): boolean | undefined {
    if (mail === undefined) return;
    return GetEmailError(mail) === undefined;
}

/** Words that mark a 400 as being about the email field. */
const serverEmailErrorPattern = /e-?mail|username|user name|address/i;

/**
 * Decides whether a rejected request was a validation failure on the email
 * field, so the page can render it on the input instead of in a banner.
 *
 * Client rules will drift from server rules over time, so a 400 the client
 * thought was fine still has to reach the user next to the field they can fix.
 * Returns the message to display, or undefined if this is not an email error.
 */
export function GetServerEmailError(
    statusCode: string | number | undefined | null,
    message: string | undefined | null
): string | undefined {
    if (statusCode?.toString() !== "400") return undefined;
    const trimmed = (message ?? "").trim();
    if (trimmed === "") return undefined;
    return serverEmailErrorPattern.test(trimmed) ? trimmed : undefined;
}

export function ValidatePhoneNumber(phoneNumber: string | undefined) {
    let phoneNumberMaxLength: number = 15;

    if (phoneNumber === undefined) {
        return
    }
    if (/^(\+[0-9]+)?[0-9]*$/.test(phoneNumber) && phoneNumber.length < phoneNumberMaxLength) {
        return (true)
    }
    return (false)
}

export function ValidateBirthDate(birthDate: string | undefined, minDate: string, maxDate: string) {
    if (birthDate === undefined) {
        return
    }

    var inputDate = new Date(birthDate)
    var sixteenYearsBeforeNow = new Date(maxDate)
    var hundredYearsBeforeNow = new Date(minDate)


    if (inputDate < sixteenYearsBeforeNow && inputDate > hundredYearsBeforeNow) {
        return (true)
    }

    return (false)
}

export function ValidatePassword(newPassword: string, confirmPassword: string) {
    if (newPassword === confirmPassword) {
        return true;
    }
    else {
        return false;
    }
}

export function ValidatePasswordContent(password?: string) {
    if (password === undefined) {
        return true;
    }
    else if (/^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{8,}$/.test(password)) {
        return true;
    }
    else {
        return false;
    }
}